import { Grid, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import * as yup from 'yup'
import { isEmpty } from 'lodash'
import { useNavigate, useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faLinkSlash,
  faSquareSliders,
} from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import {
  BoxV2,
  ButtonV2,
  TextInputV2,
  Chip,
  ExternalLink,
  ModalConfirmV2,
  ModalSizeVariants,
  SwitchV2,
  SwitchVarient,
} from '@labrav/react-components'
import {
  Credentials,
  CRM,
} from '../../modules/crmSelector/action'
import { useStyles } from './CRMConfiguration.styles'
import {
  CRMSchema,
  deleteCRMAuth,
  patchCRMAuth,
  postCRMAuth,
} from '../../modules/crmAuth/crmConnectionActions'
import { selectCRMAuthKeyValue } from '../../modules/crmAuth/selectors'
import { getMappingValues } from '../../modules/crmAceMapping/getMappingValues'
import { yupObjectShaper } from '../../utils/yupObjectShaper'
import {
  baseURL,
  crmEnv,
  salesforceClientID,
  salesforceLoginURL,
  salesforceLoginStagingUrl,
  salesforceRedirectURI,
  HubspotClientUrl,
  salesforceCanvasClientID,
} from '../../utils/constants'
import { generateCodeChallenge } from '../../utils/generateCodeChallenge'
import { updateAppAlert } from '../../modules/appAlert/actions'
import {
  capitalizeFirstLetter,
  openSignInWindow,
  windowObjectReference,
} from '../../utils/signInWindow'
import { getAuthData } from '../../utils/getAuthData'
import { isLoading as loading } from '../../utils/loadingState'
import { LoadingTypes } from '../../modules/loading/reducer'
import { useCrmAuthInfo } from '../../utils/hooks/useCrmAuthInfo'
import { PartnerType } from '../../types/partner'
import { errorLogger } from '../../utils/errorLogger'
import StepsLabraApp from '../StepsLabraApp/StepsLabraApp'
import ConfigureLabraSalesforceAutomations from '../StepsLabraApp/ConfigureLabraSalesforceAutomations'
import { generateRandomString } from '../../utils/strings'
import Persona from '../Persona/Persona'
import { CloudTypeEnum as CloudType } from '../../types'
import { useSalesforceEventBridgeStatus } from '../../utils/hooks/useSalesforceEventBridgeStatus'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'

type Props = {
  crm: CRM | string
  isAdmin: boolean
}
type ReqState = {
  partnerId: string
  crmId: string
  isInternal: boolean
  partnerType: PartnerType
}
type salesforceState = {
  authKeyValue: string
  storedCrmType: 'salesforce' | 'hubspot' | 'connectwise' | string
  environmentType: crmEnv | string
  deletedAt: string
  lastConnectionAt?: string
  appLink?: string
  crmSchema?: CRMSchema
}

export const renderConnectionLabel = (isLoading: boolean, label: string) => {
  return <>{isLoading ? <b>...</b> : label}</>
}

export const CRMConfiguration: React.FC<Props> = ({ crm, isAdmin }) => {
  const { classes } = useStyles()
  const dispatch =
    useDispatch<ThunkDispatch<CrmOnboardingRootState, unknown, AnyAction>>()
  const globalState = useSelector((state: CrmOnboardingRootState) => state)
  const navigate = useNavigate()
  const [openModalConfirm, setopenModalConfirm] = React.useState(false)
  const disconnectModalTitle = 'Disconnect your CRM'
  const disconnectModalAcceptButton = 'Disconnect'
  const salesforceURL = (env: crmEnv) =>
    `${
      env === crmEnv.PRODUCTION ? salesforceLoginURL : salesforceLoginStagingUrl
    }?client_id=${salesforceClientID}&redirect_uri=${salesforceRedirectURI}&response_type=code&immediate=false&display=popup`

  const salesforceCanvasAppUrl = (env: crmEnv) =>
    `${
      env === crmEnv.PRODUCTION ? salesforceLoginURL : salesforceLoginStagingUrl
    }?client_id=${salesforceCanvasClientID}&redirect_uri=${salesforceRedirectURI}&response_type=code&immediate=false&display=popup`

  const handleOpenModal = () => {
    setopenModalConfirm(true)
  }

  const handleCloseModal = () => {
    setopenModalConfirm(false)
  }
  const { partnerId, crmId, isInternal, partnerType } = useSelector(
    (state: CrmOnboardingRootState) => {
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      return {
        partnerId:
          state.AccountData?.[partnerType]?.partnerData?.partnerId || '',
        crmId: state.AccountData?.[partnerType]?.partnerData?.crmId || '',
        isInternal:
          state.AccountData?.[partnerType]?.partnerData?.isInternal || false,
        partnerType: partnerType,
      }
    }
  )
  const showText = ['login_company_id', 'login_url']
  const {
    authKeyValue,
    storedCrmType,
    environmentType,
    deletedAt,
    lastConnectionAt,
    appLink,
    crmSchema,
  } = useSelector((state: CrmOnboardingRootState) => ({
    authKeyValue: selectCRMAuthKeyValue(state.CRMAuth.crmFormData?.creds),
    storedCrmType: state.CRMAuth.crmFormData?.crm_type || '',
    environmentType: state.CRMAuth.crmFormData?.environment_type || '',
    deletedAt: state.CRMAuth.crmFormData?.deleted_at || '',
    lastConnectionAt: state.CRMAuth.crmFormData?.updated_at,
    appLink: state.CRMAuth.crmFormData?.crm_app_creds?.app_link,
    crmSchema: state?.CRMAuth?.crmFormData?.crm_schema,
  }))
  const isNewVersionSalesForce = crmSchema?.new_platform_sync ?? false

  const isLoading = useSelector((state: CrmOnboardingRootState) =>
    loading(state.loading)(LoadingTypes.CRM_AUTH)
  )
  const { search: queryString, pathname } = useLocation()
  const code = new URLSearchParams(queryString).get('code')
  const [environment, setEnvironment] = useState<crmEnv>()
  const [salesforceCodeVerifier, setSalesforceCodeVerifier] = useState<string>()
  const stateCrmType = typeof crm !== 'string' ? crm.crm_type : crm

  const { isCrmSelected } = useCrmAuthInfo(crmId)

  useEffect(() => {
    const savedSalesforceEnv = localStorage.getItem('salesforce_env')
    if (savedSalesforceEnv) {
      setEnvironment(savedSalesforceEnv as crmEnv)
    }
    const savedSalesforceCodeVerifier = localStorage.getItem(
      'salesforce_code_verifier'
    )
    if (savedSalesforceCodeVerifier) {
      setSalesforceCodeVerifier(savedSalesforceCodeVerifier)
    }
  }, [])
  //For sending code_verifier, code and redirect_uri to backend
  useEffect(() => {
    // do not run this useEffect if all the values are not ready
    if (code && partnerId && !isLoading) {
      let data = [
        {
          auth_key_name: 'code',
          auth_key_value: code,
        },
      ]
      if (crm === 'salesforce') {
        if (salesforceCodeVerifier && environment) {
          data = data.concat([
            {
              auth_key_name: 'redirect_uri',
              auth_key_value: salesforceRedirectURI,
            },
            {
              auth_key_name: 'code_verifier',
              auth_key_value: salesforceCodeVerifier,
            },
          ])
          submitCRMAuthFields(data, environment)
        }
      } else {
        submitCRMAuthFields(data)
      }
    }
  }, [code, partnerId, environment])

  useEffect(() => {
    if (crmId) dispatch(getMappingValues(crmId))
  }, [deletedAt, crmId])

  //For initializing formik initial values.
  const initialVal =
    typeof crm !== 'string'
      ? Object.fromEntries(
          crm?.credential_fields?.map(c => {
            return [c.key, '']
          })
        )
      : ''

  //Creating yup validation schema
  const validationSchema =
    typeof crm !== 'string'
      ? yup.object().shape({ ...yupObjectShaper(crm) })
      : ''

  //Formik for handling form
  const formik = useFormik({
    initialValues: { ...initialVal },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: values => {
      submitCRMAuthFields(values)
    },
  })
  //For submitting CRM Authentication Fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submitCRMAuthFields = async (values: any, env?: crmEnv) => {
    const extraValues = {
      status: 'pending',
      isInternal,
      use_salesforce_canvas: isNewVersionSalesForce,
    }

    const authData = getAuthData(
      stateCrmType,
      crm,
      partnerId,
      values,
      extraValues,
      env
    )

    if (isEmpty(crmId)) {
      await dispatch(postCRMAuth(authData, partnerId))
    } else if (!isEmpty(crmId)) {
      await dispatch(patchCRMAuth(crmId, authData, partnerId))
    }

    localStorage.removeItem('salesforce_env')
    localStorage.removeItem('salesforce_code_verifier')
    // cleanup the search params
    navigate({ search: '' }, { replace: true })
  }
  useMemo(() => formik.resetForm(), [crm])

  const crmAuthFields =
    typeof crm !== 'string' &&
    crm.credential_fields.map((creds: Credentials) => (
      <Grid key={creds.key} className={classes.credsField} size={12}>
        <div className={classes.textInput}>
          <TextInputV2
            isRequired={false}
            isReadOnly={isCrmSelected}
            type={showText.includes(creds.key) ? 'text' : 'password'}
            keyName={creds.key}
            title={`${creds.label}${creds.required ? '' : ' (Optional)'}`}
            placeHolder={''}
            value={creds?.value ? creds.value : formik.values[creds.key]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isError={
              (formik.touched[creds.key] &&
                Boolean(formik?.errors[creds.key])) ||
              false
            }
            errorText={formik.errors[creds.key]}
          />
        </div>
        <div className={classes.credsHelp}>
          <div className={classes.credsHelpText}>{creds.help_text}</div>
          {creds.link && (
            <ExternalLink href={creds.link} noopener noreferrer>
              Know more about {creds.label}
            </ExternalLink>
          )}
        </div>
      </Grid>
    ))
  //Window event listener
  const receiveMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== baseURL) {
        console.error('Event is coming from unauthorized source.', {
          origin: event.origin,
          baseURL,
        })
        return
      }
      const { data } = event

      if (
        data.source === 'callback' &&
        data.params !== undefined &&
        !isEmpty(data.params)
      ) {
        // add the search params to the url
        navigate({ search: data.params }, { replace: true })
      }
    },
    [pathname]
  )

  //Close pop up if crmId is there.
  useEffect(() => {
    if (!isEmpty(crmId)) {
      windowObjectReference?.close()
    }
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
  }, [crmId])

  const handleClickToDisconnectCRM = () => {
    dispatch(deleteCRMAuth(partnerId, crmId, partnerType))
    handleCloseModal()
  }
  const urlBaseOnCRMType = () => {
    switch (stateCrmType) {
      case 'hubspot':
        return HubspotClientUrl
      default:
        return baseURL
    }
  }

  const getSalesforceAuthBaseUrl = (env: crmEnv) => {
    return isNewVersionSalesForce
      ? salesforceCanvasAppUrl(env)
      : salesforceURL(env)
  }
  const openSignInWindowSalesForce = (env: crmEnv) => {
    const codeVerifier = generateRandomString(128)
    localStorage.setItem('salesforce_code_verifier', codeVerifier)
    localStorage.setItem('salesforce_env', env)
    setSalesforceCodeVerifier(codeVerifier)
    setEnvironment(env)
    generateCodeChallenge(codeVerifier)
      .then(challenge => {
        const baseUrl = getSalesforceAuthBaseUrl(env)
        const url = `${baseUrl}&code_challenge=${encodeURIComponent(
          challenge
        )}&code_challenge_method=S256&env=${encodeURIComponent(env)}`
        openSignInWindow(stateCrmType, url, receiveMessage)
      })
      .catch(async error => {
        await dispatch(
          updateAppAlert({
            message: error,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        errorLogger({ globalState })(error as Error)
      })
  }

  const openSignInWindowDefault = (url: string) => {
    openSignInWindow(stateCrmType, url, receiveMessage)
  }

  const openSignInBaseOnCRM = (url: string, env: crmEnv) => {
    switch (stateCrmType) {
      case 'salesforce':
        return openSignInWindowSalesForce(env)

      default:
        return openSignInWindowDefault(url)
    }
  }

  const handleClickToConnectCRM = async (env: crmEnv) => {
    const url = urlBaseOnCRMType()
    if (url) openSignInBaseOnCRM(url, env)
  }

  const fetchSalesforceEventBridgeStatusEnabled =
    isCrmSelected &&
    storedCrmType === 'salesforce' &&
    isNewVersionSalesForce &&
    !!crmId

  const salesforceEventBridge = useSalesforceEventBridgeStatus({
    crmId,
    fetchEnabled: fetchSalesforceEventBridgeStatusEnabled,
    authMode: 'labra',
  })

  const showPersonaPill =
    storedCrmType === 'salesforce' &&
    isNewVersionSalesForce &&
    fetchSalesforceEventBridgeStatusEnabled &&
    !salesforceEventBridge.isLoading &&
    salesforceEventBridge.isRelayEnabled === false

  const showConfigureLabraSalesforceAutomations =
    isCrmSelected &&
    storedCrmType === 'salesforce' &&
    isNewVersionSalesForce &&
    Boolean(authKeyValue || appLink)

  //For rendering whole form
  return (
    <div>
      <BoxV2
        heading="CRM configuration"
        headingIcon={
          <FontAwesomeIcon
            icon={faSquareSliders}
            style={{ height: '20px', width: '13px' }}
          />
        }
        actions={
          showPersonaPill ? (
            <div style={{ marginTop: 4 }}>
              <Persona
                memberType="integration"
                cloudType={CloudType.AWS}
                displayLabel="Salesforce"
              />
            </div>
          ) : undefined
        }
      >
        <ModalConfirmV2
          title={disconnectModalTitle}
          variant={ModalSizeVariants.small}
          icon={
            <FontAwesomeIcon
              icon={faLinkSlash}
              style={{ height: '20px', width: '13px' }}
            />
          }
          description={
            'If you disconnect CRM your integration will break and you will not receive data in your CRM.'
          }
          open={openModalConfirm}
          titleAcceptButton={disconnectModalAcceptButton}
          onAccept={handleClickToDisconnectCRM}
          onDecline={handleCloseModal}
        />
        <div className={classes.configWrapper}>
          <div style={{ margin: '0px' }}>
            {!isCrmSelected && (
              <Typography variant="body2" className={classes.paragraph}>
                {typeof crm === 'string'
                  ? `Please click the button below to link your ${crm} account.`
                  : `Please enter your ${crm.crm_type} details for connection below:`}
              </Typography>
            )}
            {isCrmSelected && (
              <GetReadOnlyData
                crmType={storedCrmType}
                crmEnv={environmentType as crmEnv}
                authKeyValue={authKeyValue}
                lastConnectionAt={lastConnectionAt}
              />
            )}
            {typeof crm !== 'string' ? (
              <form onSubmit={formik.handleSubmit}>
                {!isCrmSelected && (
                  <Grid container spacing={2}>
                    {crmAuthFields}
                  </Grid>
                )}
                <div className={classes.buttonGroup}>
                  <ButtonV2
                    type="submit"
                    styleType={isCrmSelected ? 'outlined' : 'primary'}
                    data-testid="button"
                    onClick={() => {
                      isCrmSelected ? handleOpenModal() : null
                    }}
                    disabled={
                      isCrmSelected
                        ? false
                        : !(formik.isValid && formik.dirty) || isLoading
                    }
                  >
                    {isLoading ? (
                      <b>...</b>
                    ) : isCrmSelected && !isAdmin ? (
                      `Disconnect your ${capitalizeFirstLetter(
                        crm.crm_type
                      )} account`
                    ) : (
                      'Save and Check Connection'
                    )}
                  </ButtonV2>
                </div>
              </form>
            ) : (
              <div className={classes.buttonGroup}>
                {isCrmSelected && !isAdmin && (
                  <ButtonV2
                    styleType="outlined"
                    color="primary"
                    id="sign-in-link"
                    data-testid={`button-${crm}`}
                    onClick={handleOpenModal}
                  >
                    {renderConnectionLabel(
                      isLoading,
                      `Disconnect your ${capitalizeFirstLetter(crm)} account`
                    )}
                  </ButtonV2>
                )}

                {!isCrmSelected && (
                  <>
                    <ButtonV2
                      styleType="primary"
                      color="primary"
                      id="sign-in-link"
                      data-testid={`button-${crm}`}
                      className={`${classes.button} ${classes.salesforceButton}`}
                      onClick={() => handleClickToConnectCRM(crmEnv.STAGING)}
                    >
                      {renderConnectionLabel(
                        isLoading,
                        `Connect your ${capitalizeFirstLetter(crm)} ${
                          crm === 'salesforce' ? 'Sandbox' : ''
                        } Environment`
                      )}
                    </ButtonV2>
                    {crm === 'salesforce' && (
                      <>
                        <Typography className={classes.conditional}>
                          OR
                        </Typography>
                        <ButtonV2
                          color="primary"
                          styleType="primary"
                          id="sign-in-link"
                          data-testid={`button-${crm}`}
                          className={`${classes.button} ${classes.salesforceButton}`}
                          onClick={() =>
                            handleClickToConnectCRM(crmEnv.PRODUCTION)
                          }
                        >
                          {renderConnectionLabel(
                            isLoading,
                            `Connect your ${capitalizeFirstLetter(
                              crm
                            )} Production Environment`
                          )}
                        </ButtonV2>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </BoxV2>
      <div className={classes.dividerMargin}></div>
      {isCrmSelected && appLink && crm === 'salesforce' && (
        <StepsLabraApp appLink={appLink} data-testid="steps_component" />
      )}
      {showConfigureLabraSalesforceAutomations && (
        <>
          <div className={classes.dividerMargin}></div>
          <ConfigureLabraSalesforceAutomations
            appLink={authKeyValue || appLink || ''}
            prefetchedEventBridgeStatus={salesforceEventBridge}
          />
        </>
      )}
      <div className={classes.dividerMargin}></div>
    </div>
  )
}

type GetReadOnlyDataParams = {
  crmType: string
  crmEnv?: crmEnv
  authKeyValue: string
  lastConnectionAt?: string
}

export const GetReadOnlyData = React.memo(
  ({
    crmType,
    crmEnv,
    authKeyValue,
    lastConnectionAt,
  }: GetReadOnlyDataParams) => {
    const { classes } = useStyles()
    const title = {
      hubspot: 'Portal ID:',
      salesforce: 'Instance URL:',
      connectwise: 'Login URL:',
    }[crmType]
    const chipText = {
      hubspot: 'Connected to Hubspot account',
      connectwise: 'Connected to Connectwise account',
      salesforce: `Connected to Salesforce ${crmEnv || ''} environment`,
    }[crmType]
    const _lastConnectionAt = DateTime.fromISO(lastConnectionAt || '')
    const lastConnectionFormatted = _lastConnectionAt.isValid
      ? _lastConnectionAt.setLocale('en-US').toRelative()
      : undefined
    return (
      <div
        className={classes.detailsConnection}
        data-testid="details-connection"
      >
        <div>
          <div className={classes.detailsConnectionContent}>
            <div className={classes.detailsConnectionContentChild}>
              <span className={classes.detailsConnectionTitle}>{title}</span>{' '}
              <span className={classes.detailsConnectionContent}>
                {authKeyValue}
              </span>
            </div>
            <Chip type="success" className={classes.detailsConnectionValue}>
              {chipText}
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ height: 20, width: 14 }}
              />
            </Chip>
          </div>
        </div>
        {lastConnectionFormatted && (
          <div className={classes.lastConnection}>
            Last check: {lastConnectionFormatted}
          </div>
        )}
      </div>
    )
  }
)
